import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Divider,
  styled,
} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CloseIcon from "@mui/icons-material/Close";
import { CustomButton } from "@/components/custom/custom-button";
import { CustomErrorMessage } from "./custom-error-message";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: "12px",
    padding: theme.spacing(1),
    maxWidth: 460,
    width: "100%",
  },
}));

const IconWrapper = styled(Box)(({ theme, variant }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 44,
  height: 44,
  borderRadius: "10px",
  flexShrink: 0,
  backgroundColor:
    variant === "danger" ? theme.palette.error.main + "18" : "#fef3c7",
}));

const getVariantIcon = (variant, iconSx) => {
  if (variant === "danger") {
    return (
      <ErrorOutlineIcon sx={{ fontSize: 22, color: "error.main", ...iconSx }} />
    );
  }

  return (
    <WarningAmberIcon sx={{ fontSize: 22, color: "#d97706", ...iconSx }} />
  );
};

const CustomConfirmDialog = ({
  open,
  onClose,
  title,
  description,
  confirmLabel = "Confirm",
  variant = "warning",
  mutation,
  onConfirm,
}) => {
  const isError = mutation?.isError;
  const isPending = mutation?.isPending;
  const isSuccess = mutation?.isSuccess;

  if (isSuccess && open) {
    mutation?.reset?.();
    onClose();
  }

  const handleClose = () => {
    if (isPending) return;
    mutation?.reset?.();
    onClose();
  };

  return (
    <StyledDialog open={open} onClose={handleClose}>
      <DialogContent sx={{ pt: 2, px: 2, pb: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
          <IconWrapper variant={variant}>{getVariantIcon(variant)}</IconWrapper>

          <Box sx={{ flex: 1 }}>
            <DialogTitle sx={{ p: 0, fontSize: "1rem", fontWeight: 600 }}>
              {title}
            </DialogTitle>

            <Typography variant="caption" color="text.secondary">
              {description}
            </Typography>
          </Box>

          <IconButton
            size="small"
            onClick={handleClose}
            disabled={isPending}
            sx={{
              alignSelf: "flex-start",
              mt: -0.5,
              mr: -0.5,
              color: "text.secondary",
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Divider />

        {isError && (
          <CustomErrorMessage
            message={
              (Array.isArray(mutation?.error?.details)
                ? mutation.error.details[0]
                : null) ??
              mutation?.error?.message ??
              "Something went wrong. Please try again."
            }
          />
        )}
        {}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 2, gap: 1 }}>
        <CustomButton
          variantType="outline"
          size="small"
          onClick={handleClose}
          disabled={isPending}
        >
          Cancel
        </CustomButton>

        <CustomButton
          variantType={variant === "danger" ? "destructive" : "default"}
          size="small"
          onClick={onConfirm}
          disabled={isPending}
          sx={{ minWidth: 120 }}
        >
          {isPending ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={14} sx={{ color: "#fff" }} />
              Please wait...
            </Box>
          ) : (
            confirmLabel
          )}
        </CustomButton>
      </DialogActions>
    </StyledDialog>
  );
};

export default CustomConfirmDialog;
